import { useEffect } from 'react';
import { Input, useLocale } from 'shared-ui';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { Form } from 'ui/form/Form';
import { ReportModal } from '../reportModal/ReportModal';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { AppMessages } from 'i18n/messages';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { useRenderMultiAutocompleteOption } from 'ui/multiAutocomplete/hooks/useRenderMultiAutocompleteOption';
import { MultiAutocomplete } from 'ui/multiAutocomplete/MultiAutocomplete';
import { ReactComponent as ReportPermissionsSummaryIcon } from 'assets/icons/report-permissions.svg';
import { ReportTile } from '../reportTile/ReportTile';
import * as Styled from '../Reports.styles';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { ReportErrorAlert } from '../reportErrorAlert';

import { PermissionsSummaryReportForm, PermissionsSummaryReportProps } from './PermissionsSummaryReport.types';

export const PermissionsSummaryReport = ({
  servers,
  isModalOpen,
  onModalClose,
  onModalOpen,
  terminals,
  isLoadingTerminals,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: PermissionsSummaryReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const { control, setValue, watch } = useFormContext<PermissionsSummaryReportForm>();

  const { renderMultiAutocompleteOption } = useRenderMultiAutocompleteOption();
  const { serverOptions } = useAppSelectOptions({ servers });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (hasNoDataError && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, onResetError]);

  const terminalsOptions = terminals.map((terminal) => ({
    label: terminal.nazwa,
    value: terminal.id,
    number: terminal.numerLogicznyKontrolera,
  }));

  return (
    <>
      {perms?.ZESTAWIENIE_UPRAWNIEN?.view && (
        <ReportTile
          icon={<ReportPermissionsSummaryIcon />}
          name={formatMessage({ id: AppMessages['reports.permissionsSummary'] })}
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.permissionsSummary.title'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured}
        isDownloadButtonDisabled={isLoadingDownload}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="nazwisko"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.permissionsSummary.lastName'] })}
                placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.lastName.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="numerKarty"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.permissionsSummary.cardNumber'] })}
                placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.cardNumber.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="blocked"
            control={control}
            render={({ field }) => (
              <FormControl>
                <Styled.RadioGroupLabel>
                  {formatMessage({ id: AppMessages['reports.permissionsSummary.blocked'] })}
                </Styled.RadioGroupLabel>
                <RadioGroup row value={field.value} onChange={field.onChange} name="blocked">
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={formatMessage({ id: AppMessages['common.yes'] }).toUpperCase()}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={formatMessage({ id: AppMessages['common.no'] }).toUpperCase()}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          <Styled.FieldContainer>
            <Controller
              name="komorka"
              control={control}
              render={({ field }) => (
                <OrganizationsTreeSelect
                  {...field}
                  multiple={false}
                  placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.cell.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.permissionsSummary.cell'] })}
                />
              )}
            />
          </Styled.FieldContainer>

          <Form.ControlledSelect
            name="serwerId"
            control={control}
            onChange={(e) => {
              setValue('serwerId', e.target.value as string, { shouldDirty: true });
              setValue('tkds', []);
            }}
            label={formatMessage({ id: AppMessages['reports.permissionsSummary.server'] })}
            withHelperText
            options={serverOptions}
          />

          <Controller
            name="tkds"
            control={control}
            render={({ field }) => (
              <MultiAutocomplete
                {...field}
                withLimitedTags
                stopRotatePopupIndicator
                inputSize="big"
                limitTags={1}
                onChange={(_, option) => (option ? field.onChange(option) : field.onChange(null))}
                options={terminalsOptions}
                loading={isLoadingTerminals}
                label={formatMessage({ id: AppMessages['reports.permissionsSummary.reader'] })}
                placeholder={formatMessage({ id: AppMessages['reports.permissionsSummary.reader.placeholder'] })}
                renderOption={renderMultiAutocompleteOption}
              />
            )}
          />
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}
      </ReportModal>
    </>
  );
};
